import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { WordDTO } from "@shared/types";
import { useLocalProfile } from "../local/useLocalProfile";
import { newRoomCode } from "../multiplayer/types";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { WORD_PACKS, getWordPack } from "../data/word-packs";
import { listCustomPacks } from "../local/customPacks";
import { AvatarPreview } from "../components/AvatarEditor";
import { GameImage } from "../game/ui/GameImage";
import { BattlePodiumScene } from "../components/BattlePodiumScene";
import { speak, startPodiumMusic, fadeOutPodiumMusic } from "../game/audio";
import { useQuizRoom } from "../quizzle/useQuizRoom";
import { buildQuestion } from "../quizzle/questionEngine";
import { CountUp } from "../quizzle/CountUp";
import { QUIZ_QUESTION_TYPES, type QuizJoinOpts } from "../quizzle/types";

// 0 = no timer (teacher controls the pace).
const TIME_OPTIONS = [0, 10, 20, 30];
const COUNT_OPTIONS = [5, 10, 15, 20];

/** Quizzle Host Display — teacher's big screen. Presenter only. */
export function QuizzleHost({ codeOverride, embedded }: { codeOverride?: string; embedded?: boolean } = {}) {
  const nav = useNavigate();
  const { profile } = useLocalProfile();
  const code = useMemo(() => codeOverride ?? newRoomCode(), [codeOverride]);
  const allPacks = useMemo(() => [...WORD_PACKS, ...listCustomPacks()], []);

  const joinOpts = useMemo<QuizJoinOpts | null>(
    () => (profile ? { code, role: "host", me: { id: getBattlePlayerId(profile.id, code, "host"), displayName: profile.displayName, avatarConfig: profile.avatarConfig } } : null),
    [profile, code],
  );
  const { client, state, error } = useQuizRoom(joinOpts);

  const question = useMemo(() => {
    if (!state?.pool || (state.phase !== "question" && state.phase !== "reveal")) return null;
    return buildQuestion(state.pool as WordDTO[], state.seed, state.questionIndex, state.settings.enabledTypes);
  }, [state?.pool, state?.seed, state?.questionIndex, state?.phase, state?.settings.enabledTypes]);

  // Host plays the word audio at question start + on reveal (re-teach).
  useEffect(() => {
    if (!question) return;
    if (state?.phase === "question" && (question.kind === "audio-to-image" || question.kind === "audio-to-english" || question.kind === "spell")) {
      speak(question.word.english, { audioUrl: question.word.audioUrl, lang: "en-US" });
    }
    if (state?.phase === "reveal") {
      speak(question.word.english, { audioUrl: question.word.audioUrl, lang: "en-US" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.phase, state?.questionIndex]);

  // Tick the countdown while a TIMED question is live.
  const [, force] = useState(0);
  useEffect(() => {
    if (state?.phase !== "question" || (state?.settings.timeLimitSec ?? 0) <= 0) return;
    const id = window.setInterval(() => force((n) => (n + 1) & 0xffff), 250);
    return () => window.clearInterval(id);
  }, [state?.phase, state?.questionIndex, state?.settings.timeLimitSec]);

  // Podium music — plays the theme (subject to the music setting).
  useEffect(() => {
    if (state?.phase === "podium") {
      startPodiumMusic();
      return () => fadeOutPodiumMusic(1200);
    }
  }, [state?.phase]);

  // ── Host background music (this device only): a lobby loop, and a soft
  //    "thinking" loop while a question is open, with a host volume slider. ──
  const lobbyAudio = useRef<HTMLAudioElement | null>(null);
  const thinkAudio = useRef<HTMLAudioElement | null>(null);
  const [musicVol, setMusicVol] = useState(() => {
    try { const v = Number(localStorage.getItem("wr_quizzle_music_vol")); return Number.isFinite(v) && v >= 0 ? Math.min(1, v) : 0.4; } catch { return 0.4; }
  });
  useEffect(() => {
    const lobby = new Audio("/audio/quizzle/quiz-lobby.mp3"); lobby.loop = true; lobby.preload = "auto";
    const think = new Audio("/audio/quizzle/answer-time.mp3"); think.loop = true; think.preload = "auto";
    lobbyAudio.current = lobby; thinkAudio.current = think;
    return () => { lobby.pause(); think.pause(); lobbyAudio.current = null; thinkAudio.current = null; };
  }, []);
  useEffect(() => {
    try { localStorage.setItem("wr_quizzle_music_vol", String(musicVol)); } catch { /* ignore */ }
    if (lobbyAudio.current) lobbyAudio.current.volume = Math.min(1, musicVol);
    // Thinking music sits softer than the lobby so it never drowns the question.
    if (thinkAudio.current) thinkAudio.current.volume = Math.min(1, musicVol * 0.55);
  }, [musicVol]);
  const musicPhase = state?.phase;
  useEffect(() => {
    const lobby = lobbyAudio.current, think = thinkAudio.current;
    if (!lobby || !think) return;
    const play = (a: HTMLAudioElement) => { a.play().catch(() => {}); };
    if (musicPhase === "lobby") { think.pause(); play(lobby); }
    else if (musicPhase === "question") { lobby.pause(); play(think); }
    else { lobby.pause(); think.pause(); }
  }, [musicPhase]);
  useEffect(() => {
    // Browsers block autoplay until a user gesture — retry on the first tap.
    const unlock = () => {
      if (musicPhase === "lobby") lobbyAudio.current?.play().catch(() => {});
      else if (musicPhase === "question") thinkAudio.current?.play().catch(() => {});
    };
    window.addEventListener("pointerdown", unlock);
    return () => window.removeEventListener("pointerdown", unlock);
  }, [musicPhase]);

  useEffect(() => { if (!profile) nav("/", { replace: true }); }, [profile, nav]);
  if (!profile) return null;

  const settings = state?.settings ?? { packIds: [], timeLimitSec: 20, questionCount: 10, enabledTypes: [] };

  if (error) {
    return <div className="quizzle-host"><p className="quizzle-error">⚠️ {error}</p>{!embedded && <button className="btn btn-ghost" onClick={() => nav("/quizzle")}>← Back</button>}</div>;
  }

  const ranked = [...(state?.players ?? [])].sort((a, b) => b.score - a.score);

  // ── QUESTION presenter ──────────────────────────────────────────────
  if (state?.phase === "question" && question) {
    const limit = settings.timeLimitSec;
    const timed = limit > 0;
    const secsLeft = state.questionStartedAt ? Math.max(0, Math.ceil(limit - (Date.now() - state.questionStartedAt) / 1000)) : limit;
    const answered = state.players.filter((p) => p.answered).length;
    const showImage = question.kind === "image-to-word" || question.kind === "spell";
    return (
      <div className="quizzle-stage">
        <div className="quizzle-stage-top">
          <span>Question {state.questionIndex + 1} / {settings.questionCount}</span>
          {timed
            ? <span className={`quizzle-timer ${secsLeft <= 5 ? "is-urgent" : ""}`}>{secsLeft}s</span>
            : <span className="quizzle-timer quizzle-timer-off">⏳ No timer</span>}
          <span>{answered} / {state.players.length} answered</span>
        </div>
        <div className="quizzle-stage-prompt">
          {showImage
            ? <GameImage className="quizzle-stage-img" src={question.word.imageUrl} alt="" fallbackLabel="?" />
            : <div className="quizzle-stage-listen">🔊</div>}
          <p className="quizzle-stage-instruction">
            {question.kind === "audio-to-image" ? "Listen — tap the picture" :
             question.kind === "audio-to-english" ? "Listen — tap the word" :
             question.kind === "image-to-word" ? "Tap the word for the picture" :
             "Spell the word!"}
          </p>
          <button className="btn btn-blue btn-pill" onClick={() => question.word.audioUrl && speak(question.word.english, { audioUrl: question.word.audioUrl, lang: "en-US" })}>🔊 Replay</button>
        </div>
        <div className="quizzle-stage-actions">
          <button className="btn btn-ghost" onClick={() => client?.toLobby()}>← Lobby</button>
          <button className="btn btn-green btn-big" onClick={() => client?.next()}>{timed ? "Skip ▶" : "Show Answer ▶"}</button>
        </div>
        <MusicVolume vol={musicVol} onChange={setMusicVol} floating />
      </div>
    );
  }

  // ── REVEAL presenter (answer + re-teach + leaderboard) ──────────────
  if (state?.phase === "reveal" && question) {
    return (
      <div className="quizzle-stage">
        <h2 className="quizzle-reveal-h">The answer is…</h2>
        <div className="quizzle-reteach quizzle-reteach-big">
          <GameImage className="quizzle-reteach-img" src={question.word.imageUrl} alt="" fallbackLabel="?" />
          <strong className="quizzle-reteach-word">{question.word.english}</strong>
          {question.word.vietnamese && <small>{question.word.vietnamese}</small>}
        </div>
        <div className="quizzle-leaderboard">
          {ranked.map((p, i) => (
            <div key={p.id} className={`quizzle-lb-row quizzle-pop ${i < 3 ? `top-${i + 1}` : ""}`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className="quizzle-lb-rank">{i + 1}</span>
              <span className="quizzle-lb-avatar"><AvatarPreview cfg={p.avatarConfig} /></span>
              <span className="quizzle-lb-name">{p.displayName}</span>
              {p.lastDelta > 0 && <span className="quizzle-lb-delta">+{p.lastDelta}</span>}
              <span className="quizzle-lb-score"><CountUp from={p.score - p.lastDelta} to={p.score} /></span>
            </div>
          ))}
        </div>
        <div className="quizzle-stage-actions">
          <button className="btn btn-ghost" onClick={() => client?.toLobby()}>← Lobby</button>
          <button className="btn btn-green" onClick={() => client?.next()}>Next ▶</button>
        </div>
      </div>
    );
  }

  // ── PODIUM ──────────────────────────────────────────────────────────
  if (state?.phase === "podium") {
    const top3 = ranked.slice(0, 3);
    return (
      <div className="quizzle-stage quizzle-stage-podium">
        <h1 className="quizzle-podium-title">🏆 Final Results</h1>
        {top3.length > 0 && (
          <div className="quizzle-podium-scene">
            <BattlePodiumScene players={top3.map((p) => ({ id: p.id, displayName: p.displayName, avatarConfig: p.avatarConfig }))} />
          </div>
        )}
        <div className="quizzle-podium-scores">
          {ranked.slice(0, 8).map((p, i) => (
            <div key={p.id} className="quizzle-lb-row">
              <span className="quizzle-lb-rank">{i + 1}</span>
              <span className="quizzle-lb-avatar"><AvatarPreview cfg={p.avatarConfig} /></span>
              <span className="quizzle-lb-name">{p.displayName}</span>
              <span className="quizzle-lb-score">{p.score}</span>
            </div>
          ))}
        </div>
        <div className="quizzle-stage-actions">
          <button className="btn btn-green btn-big" onClick={() => client?.toLobby()}>🔁 Play Again</button>
          {!embedded && <button className="btn btn-ghost" onClick={() => nav("/quizzle")}>End game</button>}
        </div>
        <p className="quizzle-dim">Back to Lobby keeps everyone in the room for another round.</p>
      </div>
    );
  }

  // ── LOBBY ───────────────────────────────────────────────────────────
  const togglePack = (id: string) => {
    const has = settings.packIds.includes(id);
    client?.setSettings({ packIds: has ? settings.packIds.filter((p) => p !== id) : [...settings.packIds, id] });
  };
  const toggleType = (id: string) => {
    const has = settings.enabledTypes.includes(id);
    const next = has ? settings.enabledTypes.filter((t) => t !== id) : [...settings.enabledTypes, id];
    if (next.length === 0) return;
    client?.setSettings({ enabledTypes: next });
  };
  const startGame = () => {
    const seen = new Set<string>();
    const pool: WordDTO[] = [];
    for (const id of settings.packIds) {
      for (const w of getWordPack(id)?.words ?? []) { if (!seen.has(w.id)) { seen.add(w.id); pool.push(w); } }
    }
    if (pool.length) client?.start(pool);
  };
  const canStart = state != null && state.players.length > 0 && settings.packIds.length > 0 && settings.enabledTypes.length > 0;

  return (
    <div className="quizzle-host">
      <header className="quizzle-host-top">
        {embedded ? <span /> : <button className="btn btn-ghost" onClick={() => nav("/quizzle")}>← Back</button>}
        <h1 className="quizzle-host-title">🧩 Quizzle</h1>
        <div className="quizzle-code-card">
          <span className="quizzle-code-label">Join code</span>
          <span className="quizzle-code-value">{code}</span>
        </div>
      </header>

      <div className="quizzle-host-grid">
        <section className="quizzle-panel">
          <h2>Settings</h2>
          <div className="quizzle-setting">
            <label>Word packs <span className="quizzle-dim">(pick one or more)</span></label>
            <div className="quizzle-pack-grid">
              {allPacks.map((p) => (
                <button key={p.id} className={`quizzle-pack ${settings.packIds.includes(p.id) ? "is-on" : ""}`} onClick={() => togglePack(p.id)}>
                  <strong>{p.title}</strong><small>{p.words.length} words</small>
                </button>
              ))}
            </div>
          </div>
          <div className="quizzle-setting">
            <label>Time per question</label>
            <div className="quizzle-pill-row">
              {TIME_OPTIONS.map((t) => <button key={t} className={`quizzle-pill ${settings.timeLimitSec === t ? "is-on" : ""}`} onClick={() => client?.setSettings({ timeLimitSec: t })}>{t === 0 ? "Off (teacher)" : `${t}s`}</button>)}
            </div>
          </div>
          <div className="quizzle-setting">
            <label>Questions</label>
            <div className="quizzle-pill-row">
              {COUNT_OPTIONS.map((n) => <button key={n} className={`quizzle-pill ${settings.questionCount === n ? "is-on" : ""}`} onClick={() => client?.setSettings({ questionCount: n })}>{n}</button>)}
            </div>
          </div>
          <div className="quizzle-setting">
            <label>Question types</label>
            <div className="quizzle-pill-row">
              {QUIZ_QUESTION_TYPES.map((q) => <button key={q.id} className={`quizzle-pill ${settings.enabledTypes.includes(q.id) ? "is-on" : ""}`} onClick={() => toggleType(q.id)}><span aria-hidden>{q.icon}</span> {q.label}</button>)}
            </div>
          </div>
          <div className="quizzle-setting">
            <label>Music volume</label>
            <MusicVolume vol={musicVol} onChange={setMusicVol} />
          </div>
        </section>

        <section className="quizzle-panel">
          <h2>Players <span className="quizzle-dim">({state?.players.length ?? 0})</span></h2>
          <p className="quizzle-dim quizzle-join-hint">Students open Quizzle → Join → enter <strong>{code}</strong></p>
          <div className="quizzle-players">
            {(state?.players ?? []).map((p) => (
              <div key={p.id} className="quizzle-player">
                <span className="quizzle-player-avatar"><AvatarPreview cfg={p.avatarConfig} /></span>
                <strong>{p.displayName}</strong>
              </div>
            ))}
            {(state?.players.length ?? 0) === 0 && <p className="quizzle-dim">Waiting for players to join…</p>}
          </div>
        </section>
      </div>

      <footer className="quizzle-host-actions">
        <button className="btn btn-green btn-big" disabled={!canStart} onClick={startGame}>▶ Start Quizzle</button>
        {!canStart && <p className="quizzle-dim">Pick at least one pack and wait for a player to join.</p>}
      </footer>
    </div>
  );
}

/** Host music volume control — inline (settings) or floating (on the stage). */
function MusicVolume({ vol, onChange, floating }: { vol: number; onChange: (v: number) => void; floating?: boolean }) {
  return (
    <div className={floating ? "quizzle-music-float" : "quizzle-vol-row"}>
      <span aria-hidden>{vol === 0 ? "🔇" : "🎵"}</span>
      <input
        type="range" min={0} max={1} step={0.05} value={vol}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Music volume"
      />
    </div>
  );
}
